/**
 * @graphql agg
 */
import { graphql } from 'react-relay';

import { useMutationInterop } from '@confluence/apollo-relay-interop';
import { expValEquals } from '@confluence/feature-experiments';

import {
	buildMutationContextForNoNetworkError,
	emptyReactionsSummary,
	OPTIMISTIC_SAVE_COMMENT_ID,
} from './commentDataManagerUtils.ts';
import type {
	useCreateObjectCommentMutation$data,
	useCreateObjectCommentMutation$variables,
} from './__generated__/useCreateObjectCommentMutation.graphql.ts';
import { type Comment } from './useAllCommentsData.ts';

// This hook returns a mutation function to create a new comment on an object. Because the new
// Comments Data Manager caches all the data for comments in Relay, we need to define the mutation
// here so that the cache can be updated fully when a new comment is created - and that means the
// mutation needs to return the full comment data structure that we are using in the returned query
// part of the mutation.  Since that returned query is quite large, we define it here in its own
// hook to avoid having it in many places in the codebase - which makes the code harder to read and
// could lead to the multiple copies getting out of sync.

// Data Mutation Definition:

// Unfortunately our Relay-friendly type generation tools don't support using GQL fragments yet -
// but in order to keep the definition of the comment fields in sync (so the cache update fragment
// is always in sync with the full query) we will need to manually make any changes to one
// definition in any others as well, until our tooling supports fragments and then we move to a
// more fragmented-based approach.  Note there are also comment queries or fragments in the
// following files that need to be kept in sync as well:
// - next/packages/comments-data-manager/src/useAllCommentsData.ts
// - next/packages/comments-data/src/hooks/useCommentById.ts
// - next/packages/comments-data-manager/src/updateCommentInCache.ts
// - next/packages/comments-data-manager/src/useCreateInlineCommentMutation.ts
// - next/packages/comments-data-manager/src/useCreateInlineReplyMutation.ts
// - next/packages/object-comments/src/components/ObjectCommentContainer.tsx

const CreateObjectCommentMutation = graphql`
	mutation useCreateObjectCommentMutation(
		$pageId: ID!
		$cloudId: ID!
		$input: CreateCommentInput!
		$includeReactionsSummary: Boolean!
	) {
		createFooterComment(cloudId: $cloudId, input: $input) {
			id
			author {
				__typename
				displayName
				permissionType
				... on KnownUser {
					accountId
					permissionType
				}
				profilePicture {
					path
				}
				type
			}
			body(representation: ATLAS_DOC_FORMAT) {
				value
			}
			container {
				id
			}
			contentStatus
			createdAt {
				value
			}
			createdAtNonLocalized
			links {
				editui
				webui
			}
			location {
				__typename
				type
				... on FooterComment {
					commentResolveProperties {
						resolved
						resolvedFriendlyDate
						resolvedTime
						resolvedUser
					}
				}
				... on InlineComment {
					inlineMarkerRef
					inlineResolveProperties {
						isDangling
						resolved
						resolvedFriendlyDate
						resolvedTime
						resolvedUser
					}
					inlineText
				}
			}
			parentId
			permissions {
				isEditable
				isRemovable
				isResolvable
			}
			reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment")
				@include(if: $includeReactionsSummary) {
				ari
				containerAri
				reactionsCount
				reactionsSummaryForEmoji {
					id
					count
					emojiId
					reacted
				}
			}
			# don't need the 'replies' field because new comments are always created without any replies
			version {
				by {
					__typename
					type
					displayName
					... on KnownUser {
						accountId
					}
				}
				friendlyWhen
				number
				when
			}
		}
	}
`;

// NOTE: Using `any` instead of `useCreateObjectCommentMutation$data` as return type.
// `useCreateObjectCommentMutation$data` type does not define all of the `__typename` fields
// (because they are not explicitly fetched in the mutation above), but actual GQL responses contain
// them. Thus, these `__typename` fields are *required* for Apollo's writeQuery to correctly merge
// the optimistic response comment with comments fetched in actual GraphQL responses.
// NOTE: useCreateObjectCommentMutation handles both creating new or replying to an existing footer
// comment. So parentCommentId is nullable (still required to be explicitly set, for clarity).
export const getOptimisticResponse = ({
	userId,
	displayName,
	adf,
	pageId,
	parentCommentId,
}: {
	userId: string;
	displayName: string;
	adf: object;
	pageId: string;
	parentCommentId: string | null;
}): any => {
	const nowISOString = new Date().toISOString();

	return {
		createFooterComment: {
			author: {
				__typename: 'KnownUser',
				accountId: userId,
				displayName,
				permissionType: null,
				profilePicture: {
					path: userId ? `/wiki/aa-avatar/${userId}` : '',
					__typename: 'Icon',
				},
				type: 'known',
			},
			body: {
				value: JSON.stringify(adf),
				__typename: 'DocumentBody',
			},
			container: {
				id: pageId,
				__typename: 'Content',
			},
			contentStatus: 'current',
			createdAt: {
				value: '',
				__typename: 'ConfluenceDate',
			},
			createdAtNonLocalized: nowISOString, // this is required to correctly sort comments
			id: OPTIMISTIC_SAVE_COMMENT_ID,
			links: {
				editui: null,
				webui: null,
				__typename: 'Map_LinkType_String',
			},
			location: {
				__typename: 'FooterComment',
				type: 'FOOTER',
				commentResolveProperties: {
					resolved: false,
					resolvedFriendlyDate: null,
					resolvedTime: null,
					resolvedUser: null,
					__typename: 'InlineCommentResolveProperties',
				},
			},
			parentId: parentCommentId,
			permissions: {
				isEditable: true,
				isRemovable: true,
				isResolvable: true,
				__typename: 'CommentPermissions',
			},
			version: {
				by: {
					__typename: 'KnownUser',
					accountId: userId,
					displayName,
					type: 'user',
				},
				friendlyWhen: null,
				when: nowISOString, // this is required to show the creation time and tooltip correctly
				number: 1,
				__typename: 'Version',
			},
			__typename: 'Comment',
		},
	};
};

export const useCreateObjectCommentMutation = () => {
	const [createObjectCommentFn] = useMutationInterop<
		useCreateObjectCommentMutation$data,
		useCreateObjectCommentMutation$variables
	>(CreateObjectCommentMutation, buildMutationContextForNoNetworkError());

	return createObjectCommentFn;
};

export const addEmptyObjectRepliesAndReactions = (
	commentData: useCreateObjectCommentMutation$data['createFooterComment'],
): Comment => {
	return commentData
		? {
				...commentData,
				contributors: [],
				replies: [],
				reactionsSummary: expValEquals(
					'confluence_fe_use_create_comment_exclude_reactions',
					'isEnabled',
					true,
				)
					? emptyReactionsSummary
					: (commentData.reactionsSummary ?? emptyReactionsSummary),
			}
		: undefined;
};
